import { CreditsRemainingPopover } from '@/components/chat/components/CreditsRemainingPopover';
import { Button } from '@heidi/design-system/button';
import { TierPracticeFilled, TierProFilled } from '@heidi/design-system/icons';
import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { useEvidenceInSessionLimits } from '@/hooks/useEvidenceInSessionLimits';
import { cn } from '@/lib/cn';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { useIntl } from 'react-intl';

export type EvidenceQuotaGlowVariant = 'healthy' | 'warning' | 'critical';

/**
 * Maps advanced-evidence session quota to a glow variant for the footer badge.
 *
 * - Green: 1 < quota ≤ 9 (healthy remaining credits)
 * - Yellow: exactly 1 remaining
 * - Red: 0 remaining
 * - No glow: quota ≥ 10 (full monthly cap)
 */
export function getEvidenceQuotaGlowVariant(
  quota: number
): EvidenceQuotaGlowVariant | null {
  if (quota === 0) return 'critical';
  if (quota === 1) return 'warning';
  if (quota > 1 && quota <= 9) return 'healthy';
  return null;
}

/** Soft halo using theme validation tokens — full class strings for Tailwind JIT. */
const GLOW_CLASSES: Record<EvidenceQuotaGlowVariant, string> = {
  healthy:
    'shadow-[0_0_4px_0.5px_color-mix(in_srgb,hsl(var(--twc-validation-success))_60%,transparent),0_0_8px_2px_color-mix(in_srgb,hsl(var(--twc-validation-success))_45%,transparent)]',
  warning:
    'shadow-[0_0_4px_0.5px_color-mix(in_srgb,hsl(var(--twc-validation-warning))_60%,transparent),0_0_8px_2px_color-mix(in_srgb,hsl(var(--twc-validation-warning))_45%,transparent)]',
  critical:
    'shadow-[0_0_4px_0.5px_color-mix(in_srgb,hsl(var(--twc-validation-destructive))_60%,transparent),0_0_8px_2px_color-mix(in_srgb,hsl(var(--twc-validation-destructive))_45%,transparent)]',
};

export function getEvidenceQuotaGlowClass(quota: number): string | undefined {
  const variant = getEvidenceQuotaGlowVariant(quota);
  return variant ? GLOW_CLASSES[variant] : undefined;
}

export interface EvidenceQuotaBadgeProps {
  className?: string;
}

/**
 * Pro-tier remaining-credits count badge for advanced-evidence session credits.
 * Self-contained: reads quota and billing tier internally.
 */
export function EvidenceQuotaBadge({
  className,
}: EvidenceQuotaBadgeProps) {
  const intl = useIntl();
  const { advancedRemaining } = useEvidenceInSessionLimits();
  const { userTier } = useQueryUserTier();
  const showPracticeVariant = userTier === SubscriptionTier.PRO_PLUS;

  const quotaGlowClass = getEvidenceQuotaGlowClass(advancedRemaining);
  const formattedCount = intl.formatNumber(advancedRemaining);
  const creditsRemainingLabel = intl.formatMessage(
    {
      id: '8jGs4MhYt4',
      defaultMessage: '{count} left',
      description:
        'Accessible label for evidence credits remaining badge; count is locale-formatted',
    },
    { count: advancedRemaining }
  );

  const TierIcon = showPracticeVariant ? TierPracticeFilled : TierProFilled;

  if (isAppleCompliantBuild()) return null;

  return (
    <CreditsRemainingPopover>
      <Button
        type="button"
        variant={showPracticeVariant ? 'pro_plus' : 'pro'}
        size="xxs"
        aria-label={creditsRemainingLabel}
        className={cn(
          showPracticeVariant ? 'bg-tier-practice-border text-tier-practice-text hover:bg-tier-practice/50' : 'bg-tier-pro-border text-tier-pro-text hover:bg-tier-pro/50',
          quotaGlowClass,
          className
        )}
      >
        <TierIcon size={12} />
        {formattedCount}
      </Button>
    </CreditsRemainingPopover>
  );
}
